import { compareSortKeys } from '../../sync-client/internal/utils/sort-key-compare';

/**
 * The deck's MATERIALS ORDER — the one rule both platforms draw the tab bar
 * and the All Sources grid by, and that a drag-to-reorder edits.
 *
 * An entry that has been placed by hand carries a `sortKey` (fractional
 * indexing, minted in `reorder.ts`); keyed entries sort by it. An entry with
 * NO key has never been placed and sorts AFTER every keyed one, in the order
 * it is stored — which is where a freshly added document, note or map lands:
 * at the end, where an append has always put it, never jumping ahead of
 * what the user arranged. Until a deck is reordered for the first time
 * nothing is keyed, and the list reads exactly as it did before: sources
 * (primary first), notes, maps.
 *
 * Ties on a key are broken by the item's own key string, so two devices that
 * minted the same key for different items still agree on ONE total order
 * without waiting for a repair — `compareCardOrder` does the same for cards.
 *
 * Read-side only: this file must never pull the key GENERATOR
 * (`fractional-indexing`, ESM-only), so every list renderer and its tests
 * stay free of it.
 */
export interface MaterialOrderEntry {
  key: string;
  sortKey?: string;
}

export function compareMaterialOrder(
  a: MaterialOrderEntry,
  b: MaterialOrderEntry,
): number {
  if (a.sortKey !== undefined && b.sortKey !== undefined) {
    return compareSortKeys(a.sortKey, b.sortKey) || compareSortKeys(a.key, b.key);
  }
  if (a.sortKey !== undefined) return -1;
  if (b.sortKey !== undefined) return 1;
  // Neither placed: stored order, kept by the stable sort.
  return 0;
}

/** A copy of `items` in materials order. Stable, so unkeyed items keep their place. */
export function sortDeckMaterials<T extends MaterialOrderEntry>(
  items: readonly T[],
): T[] {
  return [...items].sort(compareMaterialOrder);
}
